import React, { useEffect } from 'react';
import { CheckCircleIcon, PlayCircleIcon, ArrowRightIcon, AlertCircleIcon, BarChart3Icon, TargetIcon, ShieldCheckIcon, TrendingUpIcon, BuildingIcon, UserIcon } from 'lucide-react';
import { Link } from 'react-router-dom';
export interface ServicePageProps {
  badge: string;
  heroHeadline: string;
  heroSub: string;
  heroCta: string;
  problems: string[];
  videoSectionTitle: string;
  videos: {
    title: string;
    id: string;
  }[];
  howWeHelp: string[];
  process: {
    title: string;
    description: string;
  }[];
  scenario: string;
  showToolSection?: boolean;
  finalCta: string;
}
export function ServiceLandingPage({
  badge,
  heroHeadline,
  heroSub,
  heroCta,
  problems,
  videoSectionTitle,
  videos,
  howWeHelp,
  process,
  scenario,
  showToolSection = false,
  finalCta
}: ServicePageProps) {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  return <main className="pt-20 pb-24" data-id="element-317">
      {/* 1. HERO SECTION */}
      <section className="relative py-24 overflow-hidden bg-[#f8f9fb] dark:bg-[#0f1629] transition-colors duration-300" data-id="element-318">
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[400px] bg-gradient-to-b from-primary-500/10 to-transparent dark:from-primary-500/5 rounded-full blur-[100px] -z-10" data-id="element-319"></div>
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10" data-id="element-320">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white dark:bg-white/5 border border-slate-200 dark:border-white/10 shadow-sm dark:shadow-none mb-8" data-id="element-321">
            <span className="flex h-2 w-2 rounded-full bg-primary-500" data-id="element-322"></span>
            <span className="text-sm font-medium text-slate-600 dark:text-slate-300" data-id="element-323">
              {badge}
            </span>
          </div>
          <h1 className="text-4xl md:text-6xl font-extrabold tracking-tight text-slate-900 dark:text-white mb-6 leading-tight" data-id="element-324">
            {heroHeadline}
          </h1>
          <p className="text-xl text-slate-600 dark:text-slate-400 mb-10 leading-relaxed font-light max-w-3xl mx-auto" data-id="element-325">
            {heroSub}
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4" data-id="element-326">
            <Link to="/#contact" className="w-full sm:w-auto px-8 py-4 rounded-xl bg-gradient-to-r from-accent-500 to-primary-500 text-white font-semibold text-lg hover:shadow-[0_0_30px_rgba(20,184,166,0.35)] transition-all flex items-center justify-center gap-2 group hover:scale-[1.02]" data-id="element-327">
              {heroCta}
              <ArrowRightIcon className="w-5 h-5 group-hover:translate-x-1 transition-transform" data-id="element-328" />
            </Link>
          </div>
        </div>
      </section>

      {/* 2. RELATABLE PROBLEM */}
      <section className="py-20 bg-white dark:bg-[#1a2035] border-y border-slate-100 dark:border-white/5 transition-colors duration-300" data-id="element-329">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-330">
          <div className="text-center mb-12" data-id="element-331">
            <h2 className="text-3xl font-bold text-slate-900 dark:text-white mb-4" data-id="element-332">
              Sound Familiar?
            </h2>
            <p className="text-slate-500 dark:text-slate-400" data-id="element-333">
              The common challenges we help our clients overcome.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6" data-id="element-334">
            {problems.map((problem, i) => <div key={i} className="p-6 rounded-2xl bg-slate-50 dark:bg-white/5 border border-slate-200 dark:border-white/10 shadow-sm dark:shadow-none" data-id="element-335">
                <AlertCircleIcon className="w-8 h-8 text-accent-500 mb-4" data-id="element-336" />
                <p className="text-slate-700 dark:text-slate-300 font-medium" data-id="element-337">
                  {problem}
                </p>
              </div>)}
          </div>
        </div>
      </section>

      {/* 3. VIDEO SECTION */}
      <section className="py-24 bg-[#f8f9fb] dark:bg-[#0f1629] transition-colors duration-300" data-id="element-338">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-339">
          <div className="text-center mb-16" data-id="element-340">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white dark:bg-white/5 border border-slate-200 dark:border-white/10 shadow-sm dark:shadow-none mb-6" data-id="element-341">
              <PlayCircleIcon className="w-4 h-4 text-primary-500" data-id="element-342" />
              <span className="text-sm font-medium text-slate-600 dark:text-slate-300" data-id="element-343">
                Educational Series
              </span>
            </div>
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 dark:text-white mb-4" data-id="element-344">
              {videoSectionTitle}
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8" data-id="element-345">
            {videos.map((video, i) => <div key={i} className="flex flex-col gap-4" data-id="element-346">
                <div className="relative w-full pt-[56.25%] rounded-2xl overflow-hidden shadow-lg border border-slate-200 dark:border-white/10 bg-slate-200 dark:bg-slate-800" data-id="element-347">
                  <iframe className="absolute top-0 left-0 w-full h-full" src={`https://www.youtube.com/embed/${video.id}`} title={video.title} allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen data-id="element-348"></iframe>
                </div>
                <h3 className="text-lg font-semibold text-slate-900 dark:text-white px-2" data-id="element-349">
                  {video.title}
                </h3>
              </div>)}
          </div>
        </div>
      </section>

      {/* 4. HOW WE APPROACH THIS */}
      <section className="py-20 bg-white dark:bg-[#1a2035] border-y border-slate-100 dark:border-white/5 transition-colors duration-300" data-id="element-350">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-351">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center" data-id="element-352">
            <div data-id="element-353">
              <h2 className="text-3xl md:text-4xl font-bold text-slate-900 dark:text-white mb-6" data-id="element-354">
                How We Approach This
              </h2>
              <p className="text-lg text-slate-600 dark:text-slate-400 mb-8" data-id="element-355">
                We believe in strategy-first broking. It's not just about
                getting the loan approved; it's about structuring it correctly
                for your long-term goals.
              </p>
              <ul className="space-y-5" data-id="element-356">
                {howWeHelp.map((point, i) => <li key={i} className="flex items-start gap-4" data-id="element-357">
                    <div className="p-2 rounded-lg bg-primary-50 dark:bg-primary-500/10 text-primary-600 dark:text-primary-400 mt-1" data-id="element-358">
                      <TargetIcon className="w-5 h-5" data-id="element-359" />
                    </div>
                    <div data-id="element-360">
                      <p className="text-slate-700 dark:text-slate-300 font-medium text-lg" data-id="element-361">
                        {point}
                      </p>
                    </div>
                  </li>)}
              </ul>
            </div>
            <div className="relative" data-id="element-362">
              <div className="absolute inset-0 bg-gradient-to-tr from-accent-500/10 to-primary-500/10 rounded-3xl blur-2xl -z-10" data-id="element-363"></div>
              <div className="bg-slate-50 dark:bg-[#0f1629] border border-slate-200 dark:border-white/10 rounded-3xl p-8 shadow-xl dark:shadow-none" data-id="element-364">
                <div className="flex items-center gap-4 mb-6" data-id="element-365">
                  <div className="w-12 h-12 rounded-full bg-gradient-to-r from-accent-500 to-primary-500 flex items-center justify-center text-white font-bold text-xl" data-id="element-366">
                    "
                  </div>
                  <h3 className="text-xl font-bold text-slate-900 dark:text-white" data-id="element-367">
                    Real Scenario
                  </h3>
                </div>
                <p className="text-lg text-slate-600 dark:text-slate-300 italic leading-relaxed" data-id="element-368">
                  {scenario}
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 5. PROCESS */}
      <section className="py-24 bg-[#f8f9fb] dark:bg-[#0f1629] transition-colors duration-300" data-id="element-369">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-370">
          <div className="text-center mb-16" data-id="element-371">
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 dark:text-white mb-4" data-id="element-372">
              The Process
            </h2>
            <p className="text-lg text-slate-500 dark:text-slate-400 max-w-2xl mx-auto" data-id="element-373">
              A clear, step-by-step approach to getting you from where you are
              to where you want to be.
            </p>
          </div>
          <div className="max-w-4xl mx-auto" data-id="element-374">
            <div className="space-y-6" data-id="element-375">
              {process.map((step, i) => <div key={i} className="flex items-start gap-6 p-6 rounded-2xl bg-white dark:bg-[#1a2035] border border-slate-200 dark:border-white/10 shadow-sm dark:shadow-none" data-id="element-376">
                  <div className="flex-shrink-0 w-12 h-12 rounded-xl bg-primary-50 dark:bg-primary-500/10 text-primary-600 dark:text-primary-400 flex items-center justify-center font-bold text-lg border border-primary-100 dark:border-primary-500/20" data-id="element-377">
                    {i + 1}
                  </div>
                  <div data-id="element-378">
                    <h3 className="text-xl font-bold text-slate-900 dark:text-white mb-2" data-id="element-379">
                      {step.title}
                    </h3>
                    <p className="text-slate-600 dark:text-slate-400" data-id="element-380">
                      {step.description}
                    </p>
                  </div>
                </div>)}
            </div>
          </div>
        </div>
      </section>

      {/* 7. WHY WORK WITH ME */}
      <section className="py-24 bg-white dark:bg-[#1a2035] border-y border-slate-100 dark:border-white/5 transition-colors duration-300" data-id="element-381">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-382">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center" data-id="element-383">
            <div data-id="element-384">
              <h2 className="text-3xl md:text-4xl font-bold text-slate-900 dark:text-white mb-6 leading-tight" data-id="element-385">
                Strategy isn't just something I talk about —{' '}
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-accent-500 to-primary-500" data-id="element-386">
                  it's something I apply.
                </span>
              </h2>
              <p className="text-lg text-slate-600 dark:text-slate-400 mb-6 leading-relaxed" data-id="element-387">
                I didn't start this business just to write loans. I started it
                because I saw a massive gap — too many brokers are order-takers,
                focused on getting a single deal done rather than looking at the
                big picture.
              </p>
              <ul className="space-y-4 mb-8" data-id="element-388">
                {['You work directly with me', 'Every strategy is built around your situation, not a template.', 'I focus on long-term positioning, not just getting a loan approved.', 'Clear, honest communication — no jargon, no pressure.'].map((item, i) => <li key={i} className="flex items-start gap-3" data-id="element-389">
                    <CheckCircleIcon className="w-5 h-5 text-primary-500 flex-shrink-0 mt-0.5" data-id="element-390" />
                    <span className="text-slate-600 dark:text-slate-300 text-sm leading-relaxed" data-id="element-391">
                      {item}
                    </span>
                  </li>)}
              </ul>
            </div>
            <div className="grid grid-cols-2 gap-4" data-id="element-392">
              {[{
              value: 'Perth',
              label: 'BASED'
            }, {
              value: '1:1',
              label: 'PERSONALISED STRATEGY'
            }, {
              value: 'Invest',
              label: 'BUILT TO SCALE'
            }, {
              value: 'Facts',
              label: 'STRATEGY OVER HYPE'
            }].map((stat, index) => <div key={index} className="text-center p-6 rounded-2xl bg-slate-50 dark:bg-[#0f1629] border border-slate-200 dark:border-white/10 flex flex-col items-center justify-center min-h-[140px]" data-id="element-393">
                  <div className="text-3xl md:text-4xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-accent-500 to-primary-500 mb-2" data-id="element-394">
                    {stat.value}
                  </div>
                  <div className="text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider" data-id="element-395">
                    {stat.label}
                  </div>
                </div>)}
            </div>
          </div>
        </div>
      </section>

      {/* 8. TOOL INTEGRATION (Optional) */}
      {showToolSection && <section className="py-20 bg-[#f8f9fb] dark:bg-[#0f1629] transition-colors duration-300" data-id="element-396">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-id="element-397">
            <div className="p-10 rounded-3xl bg-gradient-to-br from-slate-900 to-slate-800 dark:from-[#1a2035] dark:to-[#0f1629] border border-slate-700 dark:border-white/10 shadow-2xl relative overflow-hidden" data-id="element-398">
              <div className="absolute top-0 right-0 w-64 h-64 bg-primary-500/20 rounded-full blur-[80px]" data-id="element-399"></div>
              <div className="relative z-10" data-id="element-400">
                <BarChart3Icon className="w-12 h-12 text-primary-400 mx-auto mb-6" data-id="element-401" />
                <h2 className="text-3xl font-bold text-white mb-4" data-id="element-402">
                  Portfolio Pulse Check
                </h2>
                <p className="text-slate-300 text-lg mb-8 max-w-2xl mx-auto" data-id="element-403">
                  See your equity, LVR and growth position before your next
                  move.
                </p>
                <Link to="/#tools" className="inline-flex items-center gap-2 px-8 py-4 rounded-xl bg-white text-slate-900 font-bold hover:bg-slate-100 transition-colors shadow-lg" data-id="element-404">
                  Run Your Portfolio Check
                  <ArrowRightIcon className="w-5 h-5" data-id="element-405" />
                </Link>
              </div>
            </div>
          </div>
        </section>}

      {/* 9. FINAL CTA */}
      <section className="py-24 bg-white dark:bg-[#1a2035] border-t border-slate-100 dark:border-white/5 transition-colors duration-300" data-id="element-406">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-id="element-407">
          <h2 className="text-4xl md:text-5xl font-bold text-slate-900 dark:text-white mb-6" data-id="element-408">
            Ready to Take the Next Step?
          </h2>
          <p className="text-xl text-slate-600 dark:text-slate-400 mb-10 max-w-2xl mx-auto" data-id="element-409">
            Book a complimentary 30-minute strategy session to discuss your
            position and map out your options.
          </p>
          <Link to="/#contact" className="inline-flex items-center gap-2 px-10 py-5 rounded-2xl bg-gradient-to-r from-accent-500 to-primary-500 text-white font-bold text-lg hover:shadow-[0_0_40px_rgba(20,184,166,0.4)] transition-all hover:scale-[1.02]" data-id="element-410">
            {finalCta}
            <ArrowRightIcon className="w-6 h-6" data-id="element-411" />
          </Link>
        </div>
      </section>
    </main>;
}